import { apiCommon } from 'src/api/ApiCommon';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { arrayMove } from 'react-movable';
import { MarketFilterType } from 'src/api/config/marketFilters/getMarketFiltersForSport';
import { MarketType } from 'src/api/config/marketFilters/getMarketsForSport';
import { MobxMap } from 'src_common/common/mobx-utils/MobxMap';
import { MarketFilterUpdateParams } from 'src/api/config/marketFilters/updateMarketFiltersForSport';
import moment from 'moment';
import { TypeId } from 'src_common/common/websocket2/type';

const updateOrderValue = <T extends { display_order: number }>(elements: Array<T>): Array<T> => {
    elements.map((elem, index): void => {
        elem.display_order = index;
    });

    return elements;
};

export interface MarketFromEvents {
    display_order: number;
    market_id: number;
    market_name: string;
    sport_name: string;
}
export class MarketFiltersState {
    @observable public isLoading: boolean = false;
    @observable public marketFiltersForSport: MobxMap<string, Array<MarketFilterType>>;
    @observable public selectedFilter: MarketFilterType | null = null;
    @observable public marketsForSport: MobxMap<string, Array<MarketType>>;
    @observable public eventsFromDate: Date = new Date();
    @observable public eventsEndDate: Date = new Date();

    public constructor(
        private readonly models: ModelsState,
        public readonly currentSport: string
    ) {
        this.marketFiltersForSport = new MobxMap(async (sport: string): Promise<Array<MarketFilterType>> => {
            return await apiCommon.marketFiltersGetFilters.run({ sport });
        });

        this.marketsForSport = new MobxMap(async (sport: string): Promise<Array<MarketType>> => {
            return await apiCommon.marketFiltersGetMarketsForSport.run({ sport });
        });

        this.eventsEndDate.setDate(this.eventsEndDate.getDate() + 3);
        makeAutoObservable(this);
    }

    @action public addMarketToCurrentFilter = async (marketName: string): Promise<void> => {
        const currentFilter = this.selectedFilter;
        if (currentFilter === null) {
            console.log('No selected filter => addToCurrentFilter => MarketFiltersState');
            return;
        }

        await this.addMarketToAnyFilter(marketName, currentFilter.id);
    };

    @action public addMarketToAnyFilter = async (marketName: string, filterId: number): Promise<void> => {
        const newOrder = this.marketsBySelectedFilter.length;

        const market = {
            display_order: newOrder,
            filter_id: filterId,
            market_name: marketName,
            market_slug: marketName.toLowerCase().split(' ').join('_'),
            sport_name: this.currentSport,
        };

        await apiCommon.marketFiltersAddMarketToFilter.run(market);

        await this.marketsForSport.get(this.currentSport).refresh();
    };

    @action public removeMarketFromFilter = async (marketId: number, sportName: string): Promise<void> => {
        await apiCommon.marketFiltersRemoveMarketFromFilter.run({ marketId });
        this.marketsForSport.get(sportName).refresh();
    };

    @action selectFilter = (filter: MarketFilterType): void => {
        if (this.selectedFilter?.id === filter.id) {
            this.selectedFilter = null;
        } else {
            this.selectedFilter = filter;
        }
    };

    @action changeFiltersOrderHandler = ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
        targetRect: ClientRect;
    }): void => {
        this.setAllMarketFiltersOrder(arrayMove(this.getFiltersForSport, oldIndex, newIndex));
    };

    @action setAllMarketFiltersOrder = async (filters: Array<MarketFilterType>): Promise<void> => {
        const listWithNewOrder = updateOrderValue(filters);
        const filtersForSend: Array<MarketFilterUpdateParams> = [];

        for (const filter of listWithNewOrder) {
            filtersForSend.push({ filter_id: filter.id, display_order: filter.display_order });
        }

        this.isLoading = true;
        await apiCommon.marketFiltersUpdateFilters.run({
            filters: filtersForSend,
        });

        await this.marketFiltersForSport.get(this.currentSport).refresh();
        this.isLoading = false;
    };

    @action changeMarketsOrderHandler = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
        targetRect: ClientRect;
    }): Promise<void> => {
        const newMarketsOrder = updateOrderValue(arrayMove(this.marketsBySelectedFilter, oldIndex, newIndex));
        const dataToUpdate = {
            sport: this.currentSport,
            markets: newMarketsOrder,
        };
        await apiCommon.marketFiltersUpdateMarketsForSport.run(dataToUpdate);
        this.marketsForSport.get(this.currentSport).refresh();
    };

    @action setEventsFromDate = (date: Date | null): void => {
        if (date !== null) {
            this.eventsFromDate = date;
        }
    };

    @action setEventsEndDate = (date: Date | null): void => {
        if (date !== null) {
            this.eventsEndDate = date;
        }
    };

    @action removeFilter = async (filterId: number): Promise<void> => {
        await apiCommon.marketFiltersRemoveFilter.run({ filterId });
        this.marketFiltersForSport.get(this.currentSport).refresh();
    };

    @computed private get currentSportEventsIds(): Array<number> {
        const from = moment(this.eventsFromDate);
        const to = moment(this.eventsEndDate);
        const sportModel = this.models.getEventQuery({
            state: 'open',
            sport: [this.currentSport],
            'startTime[from]': from.toISOString(),
            'startTime[to]': to.toISOString(),
        });
        const eventIdes = sportModel?.ids ?? [];

        return eventIdes;
    }

    @computed public get allMarketFromEvents(): Array<MarketFromEvents> {
        const marketsList: Array<MarketFromEvents> = [];
        const tempSet: Set<string> = new Set();
        for (const eventId of this.currentSportEventsIds) {
            const markets = this.models.getEventMarkets(eventId);
            if (markets !== null) {
                for (const market of markets) {
                    const allMarketsSize = tempSet.size;
                    tempSet.add(market.name.toLowerCase().split(' ').join('_'));
                    if (tempSet.size > allMarketsSize) {
                        marketsList.push({
                            market_id: TypeId.getMarketId(market.id),
                            market_name: market.name,
                            display_order: 0,
                            sport_name: this.currentSport,
                        });
                    }
                }
            }
        }
        return marketsList;
    }

    @computed get getFiltersForSport(): Array<MarketFilterType> {
        if (this.currentSport === '') {
            return [];
        }
        const filters = this.marketFiltersForSport.get(this.currentSport).get();
        if (filters.type === 'ready') {
            const sortedFilterers = filters.value.sort((a, b) => a.display_order - b.display_order);
            return sortedFilterers;
        }

        return [];
    }

    @computed get marketsBySelectedFilter(): Array<MarketType> {
        const currentFilter = this.selectedFilter;
        if (currentFilter === null) {
            return [];
        }

        return this.marketByCustomFilter(currentFilter.id);
    }

    marketByCustomFilter = (filterId: number): Array<MarketType> => {
        const marketForSportResponse = this.marketsForSport.get(this.currentSport).get();
        if (marketForSportResponse.type === 'ready') {
            const markets = marketForSportResponse.value.filter((market) => market.filter_id === filterId);
            return markets.sort((a, b) => a.display_order - b.display_order);
        }
        return [];
    };
}
